import { useEffect, useState } from 'react';
import { Observable } from 'rxjs';
import { CustomError } from '../../utils';

/**
 * Easily use an (rxjs) observable within react.
 *
 * @param observable$ The observable in question
 * @param initialValue An optional initial value
 *
 * @example
 *  const { value, error, isLoading } = useObservable(observable$, []);
 */
const useObservable: {
  <T>(observable$: Observable<T>): {
    value: T | undefined;
    error: CustomError | undefined;
    isLoading: boolean;
  };
  <T>(observable$: Observable<T>, initialValue: T): {
    value: T;
    error: CustomError | undefined;
    isLoading: boolean;
  };
} = <T>(observable$: Observable<T>, initialValue?: T) => {
  const [value, setValue] = useState<T | undefined>(initialValue);
  const [error, setError] = useState<CustomError>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const subscription = observable$.subscribe({
      next: (data) => {
        setIsLoading(false);
        setError(undefined);
        setValue(data);
      },
      error: (err) => {
        setIsLoading(false);
        setError(
          err instanceof CustomError
            ? err
            : new CustomError({ ...CustomError.errors.app.General, originalError: err })
        );
      },
    });

    return (): void => subscription.unsubscribe();
  }, [observable$]);

  return { value, error, isLoading };
};

export default useObservable;
